import type { ColDef, ValueGetterParams } from 'ag-grid-community';

import type {
  MergePeriodDto,
  MergeRowDto,
  RowDecisionStatus,
} from '../api/mergeResolution.types';
import type { CellKey } from '../state/types';
import { cellKey } from '../state/cellKey';
import { statusLabel } from '../state/rowStatus';
import { cellTone } from '../state/tone';

// `decisionValueFor` supplies the first term of the period value, "local
// decision value ?? Resolved ?? Mine". It returns `undefined` when the cell
// has no local decision and `null` for a CLEAR (which displays empty).
// `resolvedIsStale` says the row's `Resolved` still holds a decision that has
// since been removed; the value is then the auto value instead.
export interface BuildColumnDefsArgs {
  dimensionHeaders: string[];
  periodKeys: (string | null)[];
  label: (dateId: string | null) => string;
  toneFor: (
    rowId: string,
    dateId: string | null
  ) => ReturnType<typeof cellTone>;
  statusFor: (rowId: string) => RowDecisionStatus;
  decisionValueFor: (
    rowId: string,
    dateId: string | null
  ) => string | null | undefined;
  resolvedIsStale?: (rowId: string, dateId: string | null) => boolean;
  reopened: Set<CellKey>;
  decisionSwitchRenderer: ColDef['cellRenderer'];
  /** Add the frozen Input column; the caller sets it only when the API sent input names. */
  showInputName?: boolean;
}

const EM_DASH = '—';

/**
 * Frozen panes: status, dimensions and the Decisions switch stay on screen
 * while the period columns scroll. `Decisions` is deliberately the last
 * pinned column — the control reached for after reading the dimensions, right
 * against the freeze boundary where the values begin. The expand chevron is
 * pinned by `ConflictGrid.tsx`, which owns that column.
 */
const PINNED_LEFT = 'left' as const;

/**
 * The pinned block is sized explicitly rather than left on AG Grid's 200px
 * default: four dimensions at 200px would fill most of a ~1500px grid and
 * leave no room for the scrolling periods.
 */
const STATUS_WIDTH = 140;
/**
 * Starting width of a period column, until the grid has rows to autofit (see
 * `ConflictGrid`). Stated explicitly because `RowDetail` sizes its own cells
 * to match, so the two have to read from one place.
 */
export const PERIOD_COLUMN_WIDTH = 120;

/**
 * The AG Grid column id of a period column. Shared with `RowDetail`, which
 * looks each period's live width up by it so the expanded row follows a resize.
 */
export const periodColId = (dateId: string | null): string =>
  `period-${dateId ?? 'value'}`;

/**
 * CSS custom properties the grid publishes its live column widths through.
 *
 * The expanded row is rendered by AG Grid, which does not re-render it when
 * the caller's props change, so a width passed down as a prop only reaches an
 * open row on the next refresh, and the two grids can sit misaligned until
 * then. A custom property set on the grid element is read by the browser
 * itself, so the detail follows a drag as it happens, whatever state the grid
 * or its data is in.
 */
export const FROZEN_WIDTH_VAR = '--mr-frozen-width';

/** The custom property carrying the width of the period column `colId`. */
export const periodWidthVarForColId = (colId: string): string =>
  `--mr-w-${colId.replace(/[^A-Za-z0-9_-]/g, '_')}`;

/** The custom property carrying one period's column width. */
export const periodWidthVar = (dateId: string | null): string =>
  periodWidthVarForColId(periodColId(dateId));

/** Floor for every column but the period columns, which have their own. */
export const COLUMN_WIDTH_MIN = 54;

/**
 * Bounds on the levelled period width; see `levelledPeriodWidth`. The floor is
 * also each period column's minimum, so a drag cannot squeeze one below what
 * the boxed editable value and the Clear button beside it need (see
 * `RowDetail`).
 */
export const PERIOD_WIDTH_MIN = 104;
export const PERIOD_WIDTH_MAX = 144;

/**
 * The one width every period column is levelled to, from their individually
 * fitted widths.
 *
 * The widest wins, so no column truncates. Clamped at both ends: the ceiling
 * stops one malformed value from widening every column in the grid, and the
 * floor stops a grid of two-digit values collapsing under its date headers.
 * An empty list falls back to the declared width rather than to zero.
 */
export function levelledPeriodWidth(fittedWidths: number[]): number {
  const usable = fittedWidths.filter((width) => Number.isFinite(width));
  if (usable.length === 0) {
    return PERIOD_COLUMN_WIDTH;
  }
  const widest = Math.max(...usable);
  return Math.min(Math.max(widest, PERIOD_WIDTH_MIN), PERIOD_WIDTH_MAX);
}
/**
 * Horizontal padding inside a value cell. AG Grid's alpine theme (18px) and
 * MUI's small `TableCell` (16px) differ, so both are pinned to this number:
 * `conflictGrid.scss` overrides `--ag-cell-horizontal-padding` and `RowDetail`
 * sets `px` on its cells. Keep the two in step, or the expanded row's values
 * sit off the grid's.
 */
export const CELL_PADDING_X = 8;
const DIMENSION_WIDTH = 130;
// The Mine/Theirs toggle group measures ~115px, and AG Grid's cell padding
// takes ~34px of whatever it is given, so 150 sat right on the limit.
const DECISIONS_WIDTH = 170;

/**
 * Frozen-block headers are centred. AG Grid has no `headerAlign` option, so
 * this goes through a class on `.ag-header-cell-label` (`conflictGrid.scss`).
 */
const CENTRED_HEADER = 'mr-header-centre';

/** Period values are right-aligned so digits line up place-for-place. */
const VALUE_CELL = 'mr-cell-value';

/** The period header is right-aligned to sit over its values. */
const RIGHT_HEADER = 'mr-header-right';

/**
 * Multi-select values alongside the text operators, in one popup.
 *
 * The Set Filter builds its value list through the column's `valueGetter`,
 * which these columns use in place of `field`, so it needs no `values`
 * callback. Both filters are enterprise, as `enableRangeSelection` in
 * `ConflictGrid.tsx` already is, so this adds no new licensing obligation.
 */
const TEXT_AND_SET_FILTER: Pick<ColDef, 'filter' | 'filterParams'> = {
  filter: 'agMultiColumnFilter',
  filterParams: {
    // Popup order: the text operators first, then the value list.
    filters: [
      { filter: 'agTextColumnFilter' },
      { filter: 'agSetColumnFilter' },
    ],
  },
};

const periodOf = (
  row: MergeRowDto,
  dateId: string | null
): MergePeriodDto | undefined => row.Periods.find((p) => p.DateID === dateId);

/**
 * The server's resolved value for a cell, unless a removed decision has made it
 * stale: a CONFLICT is then undecided again (`undefined`), and any other cell
 * is its auto value (Theirs for THEIRS_ONLY, otherwise Mine), as the API sends.
 */
function serverResolvedOf(
  args: BuildColumnDefsArgs,
  row: MergeRowDto,
  period: MergePeriodDto,
  dateId: string | null
): string | null | undefined {
  if (!args.resolvedIsStale?.(row.Input_RowID, dateId)) {
    return period.Resolved;
  }
  if (period.Class === 'CONFLICT') {
    return undefined;
  }
  return period.Class === 'THEIRS_ONLY' ? period.Theirs : period.Mine;
}

export function buildColumnDefs(
  args: BuildColumnDefsArgs
): ColDef<MergeRowDto>[] {
  const columns: ColDef<MergeRowDto>[] = [];

  // 0. Input name, first of the frozen block, so rows of several inputs on one tab can be told apart.
  if (args.showInputName) {
    columns.push({
      colId: 'input-name',
      headerName: 'Input',
      valueGetter: (params: ValueGetterParams<MergeRowDto>) =>
        params.data?.Input_Name ?? '',
      sortable: true,
      ...TEXT_AND_SET_FILTER,
      pinned: PINNED_LEFT,
      width: DIMENSION_WIDTH,
      headerClass: CENTRED_HEADER,
    });
  }

  // 1. Dimension columns. The values live in the `Dimensions` array, aligned
  // by index with `dimensionHeaders`, so these cannot use `field`.
  args.dimensionHeaders.forEach((header, index) => {
    columns.push({
      colId: `dimension-${index}`,
      headerName: header,
      valueGetter: (params: ValueGetterParams<MergeRowDto>) =>
        params.data?.Dimensions[index] ?? '',
      sortable: true,
      ...TEXT_AND_SET_FILTER,
      pinned: PINNED_LEFT,
      width: DIMENSION_WIDTH,
      headerClass: CENTRED_HEADER,
    });
  });

  // 2. Status, immediately left of the Decisions switch it describes.
  columns.push({
    colId: 'status',
    headerName: 'Status',
    valueGetter: (params: ValueGetterParams<MergeRowDto>) => {
      const row = params.data;
      if (!row) return '';
      return statusLabel(args.statusFor(row.Input_RowID));
    },
    sortable: true,
    ...TEXT_AND_SET_FILTER,
    pinned: PINNED_LEFT,
    width: STATUS_WIDTH,
    headerClass: CENTRED_HEADER,
  });

  // 3. Decisions — the row switch, and the last of the frozen columns. No
  // `field`: it maps to no MergeRowDto key.
  columns.push({
    colId: 'decisions',
    headerName: 'Decisions',
    cellRenderer: args.decisionSwitchRenderer,
    sortable: false,
    pinned: PINNED_LEFT,
    width: DECISIONS_WIDTH,
    headerClass: CENTRED_HEADER,
  });

  // 4. One column per period — the scrolling side of the freeze.
  for (const dateId of args.periodKeys) {
    columns.push({
      colId: periodColId(dateId),
      headerName: args.label(dateId),
      cellClass: VALUE_CELL,
      headerClass: RIGHT_HEADER,
      width: PERIOD_COLUMN_WIDTH,
      minWidth: PERIOD_WIDTH_MIN,
      valueGetter: (params: ValueGetterParams<MergeRowDto>) => {
        const row = params.data;
        if (!row) return '';
        const period = periodOf(row, dateId);
        if (!period) return '';

        const local = args.decisionValueFor(row.Input_RowID, dateId);
        if (local !== undefined) return local ?? '';

        return serverResolvedOf(args, row, period, dateId) ?? period.Mine ?? '';
      },
      cellClassRules: {
        'mr-cell-grey': (params) => toneIs(args, params.data, dateId, 'grey'),
        'mr-cell-blue': (params) => toneIs(args, params.data, dateId, 'blue'),
        'mr-cell-brown': (params) => toneIs(args, params.data, dateId, 'brown'),
        'mr-cell-red': (params) => toneIs(args, params.data, dateId, 'red'),
        'mr-cell-resolved': (params) => {
          const row = params.data;
          if (!row || !periodOf(row, dateId)) return false;
          return args.toneFor(row.Input_RowID, dateId).resolvedThisSession;
        },
        'mr-cell-reopened': (params) => {
          const row = params.data;
          if (!row || !periodOf(row, dateId)) return false;
          return args.reopened.has(cellKey(row.Input_RowID, dateId));
        },
      },
      tooltipValueGetter: (params) => {
        const row = params.data;
        if (!row) return '';
        const period = periodOf(row, dateId);
        if (!period) return '';
        return cellTooltip(
          row,
          period,
          serverResolvedOf(args, row, period, dateId),
          args.label(dateId),
          args.dimensionHeaders,
          statusLabel(args.statusFor(row.Input_RowID))
        );
      },
    });
  }

  return columns;
}

/**
 * The cell's tooltip: what this row/period intersection is, then its values,
 * then what it needs.
 *
 * A newline-joined string rather than a tooltip component: AG Grid renders
 * it as text content, and `white-space: pre-line` on `.mr-grid .ag-tooltip`
 * (`conflictGrid.scss`) makes the line breaks land. Dimensions use
 * `Header: Value`, the same pairing as `RowDetail`'s caption.
 */
function cellTooltip(
  row: MergeRowDto,
  period: MergePeriodDto,
  resolved: string | null | undefined,
  periodLabel: string,
  dimensionHeaders: string[],
  status: string
): string {
  const lines: string[] = [];

  if (row.Input_Name) {
    lines.push(`Input: ${row.Input_Name}`);
  }
  row.Dimensions.forEach((value, index) => {
    const header = dimensionHeaders[index] ?? `Dimension ${index + 1}`;
    lines.push(`${header}: ${value}`);
  });
  lines.push(`Period: ${periodLabel}`);

  lines.push('');
  lines.push(`Base: ${period.Base ?? EM_DASH}`);
  lines.push(`Mine: ${period.Mine ?? EM_DASH}`);
  lines.push(`Theirs: ${period.Theirs ?? EM_DASH}`);
  if (resolved !== undefined) {
    lines.push(`Resolved: ${resolved ?? EM_DASH}`);
  }
  lines.push(`Difference: ${formatDifference(period)}`);

  // Only when a side is genuinely absent. This is what explains a greyed-out
  // Mine or Theirs choice in the row detail, so it is worth the line.
  if (period.Missing.length > 0) {
    lines.push(`Missing: ${period.Missing.join(', ')}`);
  }

  lines.push('');
  lines.push(`Status: ${status}`);

  return lines.join('\n');
}

/** `150 (1.83%)`, falling back to the raw wire strings if either is not a number. */
function formatDifference(period: MergePeriodDto): string {
  const raw = period.Raw_Difference ?? EM_DASH;
  const pct = period.Pct_Difference;
  if (pct === null || pct === undefined) {
    return raw;
  }
  const asNumber = Number(pct);
  const shown = Number.isFinite(asNumber) ? `${asNumber.toFixed(2)}%` : pct;
  return `${raw} (${shown})`;
}

/**
 * Total width of the frozen columns in a built column list, summed from the
 * defs so it cannot drift from the widths above. It does NOT include the
 * expand chevron, which `ConflictGrid` owns — add `EXPAND_COLUMN_WIDTH` for
 * the position of the freeze boundary itself.
 */
export function pinnedWidth(cols: ColDef<MergeRowDto>[]): number {
  return cols.reduce(
    (total, col) =>
      col.pinned === PINNED_LEFT ? total + (col.width ?? 0) : total,
    0
  );
}

function toneIs(
  args: BuildColumnDefsArgs,
  row: MergeRowDto | undefined,
  dateId: string | null,
  tone: ReturnType<typeof cellTone>['tone']
): boolean {
  if (!row || !periodOf(row, dateId)) return false;
  return args.toneFor(row.Input_RowID, dateId).tone === tone;
}
